import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, Target, Heart, Users, Award, 
  Lightbulb, Rocket, CheckCircle, GraduationCap, Briefcase
} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Access for All',
      description: 'We believe every young person deserves access to quality tech education, regardless of background or zip code.'
    },
    {
      icon: Lightbulb,
      title: 'Learning by Doing',
      description: 'Theory without practice is forgettable. We teach through building real projects that solve real problems.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'We build more than coders—we build a supportive community of peers, mentors, and industry partners.'
    },
    {
      icon: Rocket,
      title: 'Career Ready',
      description: 'Every skill we teach has direct application in the workforce. We don\'t just teach code—we build careers.'
    }
  ];

  const team = [
    {
      name: 'Nyto Lacroix',
      role: 'Program Director',
      image: 'https://customer-assets.emergentagent.com/job_next-gen-code/artifacts/ctpdsi53_nytolacroix.png',
      description: 'Oversees program design and ensures students receive industry-relevant skills training.'
    },
    {
      name: 'Harold K. Brown, Jr.',
      role: 'Grant Writer & Board Director',
      image: 'https://customer-assets.emergentagent.com/job_next-gen-code/artifacts/2fciiewm_haroldbrown.png',
      description: 'Brings decades of financial expertise and a passion for community impact. Specializes in securing funding and supporting initiatives that create lasting opportunities.'
    },
    {
      name: 'Kendra N. Husband, MAEd, MBA',
      role: 'Educational Director & Instructional Facilitator',
      image: 'https://customer-assets.emergentagent.com/job_next-gen-code/artifacts/8sgw55qk_Kendrahusband.png',
      description: 'Over 20 years in education, teaching all subjects including special needs across four countries. Brings deep content area knowledge and expertise in learner development.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0A1F44] via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-6 block">
              About Us
            </span>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white leading-tight mb-6">
              We Don't Teach Code.{' '}
              <span className="gradient-text">We Build Futures.</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed">
              NextGen Tech Pathways is a workforce development nonprofit dedicated to creating real career pathways for youth through hands-on technology education.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
                Our Mission
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
                Empowering Youth Through Technology
              </h2>
              <p className="text-lg text-[#94A3B8] leading-relaxed mb-6">
                Our mission is to bridge the opportunity gap by providing underserved youth with the technical skills, professional development, and career pathways they need to succeed in the modern workforce.
              </p>
              <p className="text-lg text-[#94A3B8] leading-relaxed mb-8">
                Through our intensive 8-week programs, we transform complete beginners into confident builders who can create, deploy, and present real-world applications.
              </p>
              <div className="flex gap-4">
                <Link
                  to="/programs"
                  data-testid="about-programs-btn"
                  className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-6 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2"
                >
                  View Programs
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#00AEEF]/20 to-[#00FFE0]/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1638452033979-14fba9e17fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHx5b3V0aCUyMGNvZGluZyUyMGxhcHRvcHxlbnwwfHx8fDE3NzQ0NTk0Mjd8MA&ixlib=rb-4.1.0&q=85"
                alt="Youth coding on laptop"
                className="relative rounded-3xl border border-[#00AEEF]/20 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Our Values
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              What Drives Us
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Our core values guide everything we do—from curriculum design to community partnerships.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 hover:border-[#00FFE0]/50 transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#00AEEF]/20 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-[#94A3B8] leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Our Story
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-8">
              From Vision to Reality
            </h2>
            <div className="space-y-6 text-lg text-[#94A3B8] leading-relaxed text-left">
              <p>
                NextGen Tech Pathways was founded with a simple belief: every young person has the potential to become a tech creator, not just a tech consumer.
              </p>
              <p>
                We saw too many talented youth locked out of opportunities because they didn't have access to quality tech education or the connections needed to break into the industry. Traditional paths were either too expensive, too theoretical, or disconnected from real-world skills employers need.
              </p>
              <p>
                So we built something different—an intensive, hands-on program that takes students from zero coding experience to deploying their own applications in just 8 weeks. But more than that, we built a community that supports students throughout their journey and connects them with mentors and employers who believe in their potential.
              </p>
              <p className="text-[#FF6A00] font-semibold">
                Today, we're proud to be creating the next generation of tech talent—one cohort at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6A00]/20 to-[#00AEEF]/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-gen-code/artifacts/xzeozgum_senecahinds.png"
                alt="Seneca Hinds - Founder of NextGen Tech Pathways"
                className="relative rounded-3xl border border-[#FF6A00]/30 w-full max-w-md mx-auto shadow-[0_0_40px_rgba(255,106,0,0.2)]"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF6A00] mb-4 block">
                Meet Our Founder
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
                Seneca Hinds
              </h2>
              <p className="text-[#00FFE0] font-semibold text-lg mb-6">Founder & Executive Director</p>
              <div className="space-y-4 text-lg text-[#94A3B8] leading-relaxed">
                <p>
                  Seneca Hinds founded NextGen Tech Pathways with a vision to transform how underserved youth access technology education and career opportunities.
                </p>
                <p>
                  With a passion for community empowerment and technology, Seneca has dedicated his career to creating pathways for young people to succeed in the digital economy.
                </p>
                <p className="text-white font-medium">
                  "We don't just teach code—we build futures. Every student who walks through our doors has the potential to become a tech leader."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Our Team
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Dedicated to Student Success
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 hover:border-[#00FFE0]/50 transition-all duration-500 text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover border-4 border-[#00AEEF]/30"
                />
                <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                <p className="text-[#00FFE0] text-sm font-medium mb-4">{member.role}</p>
                <p className="text-[#94A3B8] leading-relaxed text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#00AEEF]/10 via-transparent to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
            Join Our Mission
          </h2>
          <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl mx-auto">
            Whether you're a student ready to learn, a company looking to partner, or someone who wants to support our mission—there's a place for you at NextGen Tech Pathways.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/enrollment"
              data-testid="about-cta-enroll-btn"
              className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2"
            >
              Enroll Now
              <ArrowUpRight className="w-5 h-5" />
            </Link>
            <Link
              to="/partners"
              data-testid="about-cta-partner-btn"
              className="inline-flex items-center justify-center bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 hover:text-white font-bold rounded-full px-8 py-4 transition-all duration-300 gap-2"
            >
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
